import React from "react";
import SpeedSign from "./SpeedSign";
import mapHelper from "@mrwa/rime-replacement/infrastructure/mapHelper";
import { isMatchingLayer } from "@mrwa/rime-replacement/esriControls/controls/layer/layerSynchronisationImpl";
import requestEsriModules from "@mrwa/rime-replacement/infrastructure/esriModules";
import BallBank from "./BallBank";
import RoadData from "./RoadData";
import { bufferDistanceMetres } from "../data/gpsData";
import irisRoadData from "../data/irisRoadData";
import classNames from "classnames";

import "./roadSafetyPanel.css";

const possibleSpeeds = ["110", "50"];

export default class RoadSafetyPanel extends React.Component {
  constructor(props) {
    super(props);

    this.irisDataSet = false;

    this.state = {
      speedLimit: "-",
      loading: false,
      curveSurveyFeatures: [],
      allowSelection: false,
      curveSurveyReferencePoint: null,
      pavementDetails: null,
      hazardRating: null,
      alignmentDetails: null,
      speedLimitLoading: false,
      curveSurveyLoading: false,
      speedLimitSet: false,
      pavementDetailsLoading: false,
      hazardRatingLoading: false,
      alignmentDetailsLoading: false,
    };

    this.handleSpeedChange = this.handleSpeedChange.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props.playerContext !== prevProps.playerContext) {
      this.props.playerContext.onGpsDataChanged(this.gpsDataChanged.bind(this));
      this.props.playerContext.onGpsFileChanged(this.gpsFileChanged.bind(this));
    }
    if (this.irisDataSet) return;
    if (this.props.playerContext && this.props.irisRoadLayerConfig) {
      irisRoadData.initialise(
        this.props.irisRoadLayerConfig,
        this.props.playerContext
      );
      irisRoadData.onirisDataChanged(this.roadDataChanged.bind(this));
      this.irisDataSet = true;
    }
  }

  handleSpeedChange(e) {
    this.setState({ speedLimit: e.target.value });
  }

  async updateSpeedLimitValue(currentPoint) {
    if (!this.speedLimitFeatures) return;
    if (!currentPoint) {
      this.setState({ speedLimit: "-" });
      return;
    }

    const { GeometryEngine } = await requestEsriModules({
      GeometryEngine: "esri/geometry/geometryEngineAsync",
    });

    //determine current speed limit
    const speedLimitDistances = await Promise.all(
      this.speedLimitFeatures.map(
        async (feature) =>
          await GeometryEngine.distance(currentPoint, feature.geometry)
      )
    );
    const speedLimitFeature =
      this.speedLimitFeatures[
        speedLimitDistances.indexOf(Math.min(...speedLimitDistances))
      ];
    const speedLimit = parseFloat(
      speedLimitFeature.attributes[
        this.props.speedLimitLayerConfig.speedLimitAttribute
      ]
    );
    if (speedLimit === 51 && !this.state.speedLimitSet) {
      this.setState({
        speedLimit: 110,
        speedLimitSet: true,
        allowSelection: true,
      });
    } else if (speedLimit !== 51) {
      this.setState({
        speedLimit: speedLimitFeature && speedLimit,
        speedLimitSet: false,
        allowSelection: false,
      });
    }
  }

  async updateCurveSurveyValues(currentPoint) {
    if (!this.curveSurveyFeatures) return;
    if (!currentPoint) {
      this.setState({
        curveSurveyFeatures: [],
        curveSurveyReferencePoint: currentPoint,
      });
      return;
    }

    const { GeometryEngine } = await requestEsriModules({
      GeometryEngine: "esri/geometry/geometryEngineAsync",
    });

    //determine potential curve survey features
    const bufferedPoint = await GeometryEngine.buffer(
      currentPoint,
      bufferDistanceMetres,
      "meters"
    );
    const featuresWithinBuffer = (
      await Promise.all(
        this.curveSurveyFeatures.map(async (feature) =>
          (await GeometryEngine.within(feature.geometry, bufferedPoint))
            ? feature
            : null
        )
      )
    ).filter((feature) => feature);

    this.setState({
      curveSurveyFeatures: featuresWithinBuffer,
      curveSurveyReferencePoint: currentPoint,
      curveSurveyBufferedPoint: bufferedPoint,
    });
  }

  async updatePavementDetailsValues(currentPoint) {
    if (!this.pavementDetailsFeatures) return;
    if (!currentPoint) {
      this.setState({ pavementDetails: null });
      return;
    }

    const { GeometryEngine } = await requestEsriModules({
      GeometryEngine: "esri/geometry/geometryEngineAsync",
    });

    const pavementDetailDistances = await Promise.all(
      this.pavementDetailsFeatures.map(
        async (feature) =>
          await GeometryEngine.distance(
            currentPoint,
            feature.geometry,
            "meters"
          )
      )
    );

    const minDistance = Math.min(...pavementDetailDistances);
    const pavementDetailsFeature =
      minDistance <= bufferDistanceMetres &&
      this.pavementDetailsFeatures[
        pavementDetailDistances.indexOf(minDistance)
      ];

    this.setState({
      pavementDetails: pavementDetailsFeature && {
        leftUnsealed:
          pavementDetailsFeature.attributes[
            this.props.pavementDetailsLayerConfig.leftUnsealedAttribute
          ],
        leftSealed:
          pavementDetailsFeature.attributes[
            this.props.pavementDetailsLayerConfig.leftSealedAttribute
          ],
        sealWidth:
          pavementDetailsFeature.attributes[
            this.props.pavementDetailsLayerConfig.sealWidthAttribute
          ],
        laneCount:
          pavementDetailsFeature.attributes[
            this.props.pavementDetailsLayerConfig.laneCountAttribute
          ],
        rightSealed:
          pavementDetailsFeature.attributes[
            this.props.pavementDetailsLayerConfig.rightSealedAttribute
          ],
        rightUnsealed:
          pavementDetailsFeature.attributes[
            this.props.pavementDetailsLayerConfig.rightUnsealedAttribute
          ],
      },
    });
  }

  async updateHazardRatingValues(roadData) {
    if (!this.hazardRatingFeatures) return;
    if (!roadData) {
      this.setState({ hazardRating: null });
      return;
    }

    const {
      roadNoAttribute,
      startSLKAttribute,
      endSLKAttribute,
      leftRightAttribute,
      hazardRatingAttribute,
    } = this.props.roadsideHazardRatingLayerConfig;
    //should be maximum one feature each, L and R, for the given SLK
    const applicableFeatures = this.hazardRatingFeatures.filter(
      (feature) =>
        feature.attributes[roadNoAttribute] === roadData.roadNo &&
        feature.attributes[startSLKAttribute] <= roadData.slk &&
        feature.attributes[endSLKAttribute] >= roadData.slk
    );
    const leftFeature = applicableFeatures.find(
      (feature) => feature.attributes[leftRightAttribute] === "L"
    );
    const rightFeature = applicableFeatures.find(
      (feature) => feature.attributes[leftRightAttribute] === "R"
    );

    this.setState({
      hazardRating: {
        left: leftFeature && leftFeature.attributes[hazardRatingAttribute],
        right: rightFeature && rightFeature.attributes[hazardRatingAttribute],
      },
    });
  }

  async updateAlignmentValues(currentPoint) {
    if (!this.alignmentFeatures) return;
    if (!currentPoint) {
      this.setState({ alignmentDetails: null });
      return;
    }

    const { GeometryEngine } = await requestEsriModules({
      GeometryEngine: "esri/geometry/geometryEngineAsync",
    });

    const alignmentDistances = await Promise.all(
      this.alignmentFeatures.map(
        async (feature) =>
          await GeometryEngine.distance(
            currentPoint,
            feature.geometry,
            "meters"
          )
      )
    );

    const minDistance = Math.min(...alignmentDistances);
    const alignmentFeature =
      minDistance <= bufferDistanceMetres &&
      this.alignmentFeatures[alignmentDistances.indexOf(minDistance)];

    this.setState({
      alignmentDetails: alignmentFeature && {
        verticalCurveType:
          alignmentFeature.attributes[
            this.props.geometryAlignmentLayerConfig.verticalCurveTypeAttribute
          ],
        gradePercent:
          alignmentFeature.attributes[
            this.props.geometryAlignmentLayerConfig.gradeAttribute
          ],
        verticalDesignSpeed:
          alignmentFeature.attributes[
            this.props.geometryAlignmentLayerConfig.verticalDesignSpeedAttribute
          ],
        horizontalProfile:
          alignmentFeature.attributes[
            this.props.geometryAlignmentLayerConfig.horizontalProfileAttribute
          ],
        curveRadius:
          alignmentFeature.attributes[
            this.props.geometryAlignmentLayerConfig.curveRadiusAttribute
          ],
        calculatedSafeSpeed:
          alignmentFeature.attributes[
            this.props.geometryAlignmentLayerConfig.calculatedSafeSpeedAttribute
          ],
        crossfall:
          alignmentFeature.attributes[
            this.props.geometryAlignmentLayerConfig.crossfallAttribute
          ],
      },
    });
  }

  getCurrentPoint(gpsData) {
    return gpsData && gpsData.isValidGeometry && gpsData.projectedGeometry;
  }

  async gpsDataChanged(gpsData) {
    const currentPoint = await this.getCurrentPoint(gpsData);

    //intentionally not awaiting - want these to run in parallel
    this.updateSpeedLimitValue(currentPoint);
    this.updateCurveSurveyValues(currentPoint);
    this.updatePavementDetailsValues(currentPoint);
    this.updateAlignmentValues(currentPoint);
  }

  roadDataChanged(roadData) {
    this.updateHazardRatingValues(roadData);
  }

  /**
   * Reacts to a gps file change by loading all data required for the entire video, based on the route followed
   * @param {object} gpsInformation contains all gps rows and also the current gps row, in addition to the buffered line that marks the route
   */
  async gpsFileChanged(gpsInformation) {
    this.speedLimitFeatures = null;
    this.curveSurveyFeatures = null;
    this.pavementDetailsFeatures = null;
    this.hazardRatingFeatures = null;
    this.alignmentFeatures = null;
    this.setState({
      speedLimitLoading: true,
      curveSurveyLoading: true,
      pavementDetailsLoading: true,
      hazardRatingLoading: true,
      alignmentDetailsLoading: true,
    });

    const getLayerData = async ({ uniqueIdentifier, subLayerId }) => {
      //get the layer that we're going to use for data
      const rimeLayer = mapHelper.layers.find((layer) =>
        isMatchingLayer({ uniqueIdentifier, subLayerId }, layer)
      );
      if (!rimeLayer) return [];

      //query layer data for the route
      const bufferedGeometry = await gpsInformation.bufferedGeometry;
      const query = {
        geometry: bufferedGeometry,
        spatialRelationship: "intersects",
        outFields: ["*"],
        returnGeometry: true,
      };
      return (await rimeLayer.layer.queryFeatures(query)).features;
    };

    const getCurrentPoint = () =>
      this.getCurrentPoint(this.props.playerContext.gpsInformation.current);
    getLayerData(this.props.speedLimitLayerConfig)
      .then(async (speedLimitFeatures) => {
        this.speedLimitFeatures = speedLimitFeatures;
        this.updateSpeedLimitValue(await getCurrentPoint());
      })
      .finally(() => this.setState({ speedLimitLoading: false }));
    getLayerData(this.props.curveSurveyLayerConfig)
      .then(async (curveSurveyFeatures) => {
        this.curveSurveyFeatures = curveSurveyFeatures;
        this.updateCurveSurveyValues(await getCurrentPoint());
      })
      .finally(() => this.setState({ curveSurveyLoading: false }));
    getLayerData(this.props.pavementDetailsLayerConfig)
      .then(async (pavementDetailsFeatures) => {
        this.pavementDetailsFeatures = pavementDetailsFeatures;
        this.updatePavementDetailsValues(await getCurrentPoint());
      })
      .finally(() => this.setState({ pavementDetailsLoading: false }));
    getLayerData(this.props.roadsideHazardRatingLayerConfig)
      .then(async (hazardRatingFeatures) => {
        this.hazardRatingFeatures = hazardRatingFeatures;
        this.updateHazardRatingValues(irisRoadData.currentRoadData);
      })
      .finally(() => this.setState({ hazardRatingLoading: false }));
    getLayerData(this.props.geometryAlignmentLayerConfig)
      .then(async (alignmentFeatures) => {
        this.alignmentFeatures = alignmentFeatures;
        this.updateAlignmentValues(await getCurrentPoint());
      })
      .finally(() => this.setState({ alignmentDetailsLoading: false }));
  }

  render() {
    const className = classNames("speedSelectionContainer", {
      speedSelection: this.state.allowSelection,
    });
    return (
      <div className="roadSafetyPanel">
        <div className={className}>
          {this.state.allowSelection && (
            <div className="formInput">
              <label>Speed</label>
              <select
                value={this.state.speedLimit}
                title={this.state.speedLimit}
                onChange={this.handleSpeedChange}
              >
                {possibleSpeeds.map((speed, i) => (
                  <option key={i} value={speed}>
                    {speed} km/h
                  </option>
                ))}
              </select>
            </div>
          )}
          <SpeedSign
            speedLimit={this.state.speedLimit}
            loading={this.state.speedLimitLoading}
          />
        </div>
        <BallBank
          curveSurveyLayerConfig={this.props.curveSurveyLayerConfig}
          curveSurveyFeatures={this.state.curveSurveyFeatures}
          referencePoint={this.state.curveSurveyReferencePoint}
          curveSurveyLoading={this.state.curveSurveyLoading}
          bufferedPoint={this.state.curveSurveyBufferedPoint}
          playerContext={this.props.playerContext}
          speedLimit={this.state.speedLimit}
        />
        <RoadData
          pavementDetails={this.state.pavementDetails}
          hazardRating={this.state.hazardRating}
          alignmentDetails={this.state.alignmentDetails}
          pavementDetailsLoading={this.state.pavementDetailsLoading}
          hazardRatingLoading={this.state.hazardRatingLoading}
          alignmentDetailsLoading={this.state.alignmentDetailsLoading}
          speedLimit={this.state.speedLimit}
        />
      </div>
    );
  }
}
